import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Badge from '../../components/common/Badge';
import { 
  ChevronLeft, 
  Phone, 
  MessageSquare, 
  Home, 
  Mail, 
  PenSquare, 
  Plus, 
  Calendar,
  Clock,
  AlertCircle,
  FileText,
  Star,
  MapPin,
  Globe,
  Link as LinkIcon
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

// Helper to beautify keys like googleRating -> Google Rating
const formatLabel = (key) => {
  const result = key.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

// Smart type inference for view mode
const inferAndRenderValue = (key, value) => {
  if (value === '' || value === null || value === undefined) return 'N/A';

  // 1. Known specific keys
  if (key === 'googleRating') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Star size={14} color="#F59E0B" fill="#F59E0B" />
        <span style={{ fontWeight: '600' }}>{value}</span>
      </div>
    );
  }
  
  if (key === 'businessStatus') {
    return (
      <span style={{ 
        background: value.toLowerCase() === 'operational' ? '#E8F5E9' : '#FFEBEE', 
        color: value.toLowerCase() === 'operational' ? '#2E7D32' : '#C62828',
        padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' 
      }}>
        {value}
      </span>
    );
  }
  
  if (key === 'website' || key === 'googleMapsUrl') {
    return (
      <a href={value} target="_blank" rel="noreferrer" style={{ color: 'var(--color-brand-primary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
        {key === 'website' ? <Globe size={14} /> : <MapPin size={14} />} 
        {key === 'website' ? 'Visit Website' : 'View on Maps'}
      </a>
    );
  }

  // 2. JSON Strings (like socialMedia or openingHours or arrays)
  if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
    try {
      const parsed = JSON.parse(value);
      
      if (key === 'openingHours' && Array.isArray(parsed)) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
            {parsed.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--color-border-tertiary)', paddingBottom: '2px' }}>
                <span style={{ color: 'var(--color-text-secondary)', width: '60px' }}>{item.day?.substring(0, 3)}</span>
                <span style={{ fontWeight: '500' }}>{item.hours}</span>
              </div>
            ))}
          </div>
        );
      }

      if (key === 'socialMedia' && !Array.isArray(parsed)) {
        return (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {Object.entries(parsed).map(([platform, url]) => {
              if (!url) return null;
              let Icon = LinkIcon;
              
              return (
                <a key={platform} href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', background: 'var(--color-background-secondary)', padding: '4px 8px', borderRadius: '4px' }}>
                  <Icon size={14} /> <span style={{ textTransform: 'capitalize' }}>{platform}</span>
                </a>
              );
            })}
          </div>
        );
      }

      if (Array.isArray(parsed)) {
        return (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {parsed.map((item, idx) => (
              <span key={idx} style={{ background: 'var(--color-background-secondary)', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{item}</span>
            ))}
          </div>
        );
      }
      
      return JSON.stringify(parsed);
    } catch (e) {
      // Fallback if not valid JSON
    }
  }

  // 3. Fallback to string representation
  return <span>{String(value)}</span>;
};


const LeadDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { role, user } = useAuth();
  
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followUpNote, setFollowUpNote] = useState('');
  const [followUpStatus, setFollowUpStatus] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [followUpLeadType, setFollowUpLeadType] = useState('');
  const [reminderEnabled, setReminderEnabled] = useState(false);
  
  // Set default reminder time to tomorrow 10:00 AM
  const getTomorrow10AM = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    // Format to YYYY-MM-DDTHH:MM for datetime-local input
    return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
  };
  const [reminderDateTime, setReminderDateTime] = useState(getTomorrow10AM());

  // Dynamic Lists
  const [customFieldsDef, setCustomFieldsDef] = useState([]);

  useEffect(() => {
    const fetchLeadDetail = async () => {
      try {
        const response = await api(`/leads/${id}`);
        if (response.ok) {
          const data = await response.json();
          setLead(data);
          setFollowUpStatus(data.status); 
          setFollowUpLeadType(data.leadType || '');
        }
      } catch (error) {
        console.error("Error fetching lead detail:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchOtherData = async () => {
      try {
        const [usersRes, sourcesRes, statusesRes, typesRes, customRes] = await Promise.all([
          api('/users'),
          api('/lead-sources'),
          api('/lead-statuses'),
          api('/lead-types'),
          api('/custom-fields')
        ]);

        if (usersRes.ok) setEmployees(await usersRes.json());
        if (sourcesRes.ok) setLeadSources((await sourcesRes.json()).map(s => s.name));
        if (statusesRes.ok) setLeadStatuses((await statusesRes.json()).map(s => s.name));
        if (typesRes.ok) setLeadTypes((await typesRes.json()).map(t => t.name));
        if (customRes.ok) setCustomFieldsDef(await customRes.json());
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    if (id && user) {
      fetchLeadDetail();
      fetchOtherData();
    }
  }, [id, user]);

  const [selectedTelecaller, setSelectedTelecaller] = useState('');
  const [selectedExecutive, setSelectedExecutive] = useState('');

  const handleAddFollowUp = async () => {
    if (!followUpNote) return;

    setSavingFollowUp(true);
    try {
      const response = await api(`/leads/${id}/followup`, {
        method: 'POST',
        body: {
          comment: followUpNote,
          status: followUpStatus,
          leadType: followUpLeadType,
          reminderAt: reminderEnabled && reminderDateTime ? reminderDateTime : null
        }
      });

      if (response.ok) {
        const updatedLead = await response.json();
        setLead(updatedLead);
        setFollowUpNote('');
        setReminderEnabled(false);
        alert("Follow-up saved!");
      }
    } catch (error) {
      console.error("Error saving follow-up:", error);
    } finally {
      setSavingFollowUp(false);
    }
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const handleEdit = () => {
    setEditForm({
      name: lead.name,
      phone: lead.phone,
      email: lead.email,
      status: lead.status || '',
      leadType: lead.leadType || '',
      source: lead.source || '',
      customFields: lead.customFields || {}
    });
    setIsEditing(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomEditChange = (name, value) => {
    setEditForm(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [name]: value
      }
    }));
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const response = await api(`/leads/${id}`, {
        method: 'PUT',
        body: editForm
      });

      if (response.ok) {
        const updatedLead = await response.json();
        setLead(updatedLead);
        setIsEditing(false);
        alert("Lead updated successfully!");
      } else {
        const err = await response.json();
        alert(err.message || "Update failed");
      }
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAssign = async (type, userId) => {
    try {
      const response = await api(`/leads/${id}/assign`, {
        method: 'PATCH',
        body: {
          [type === 'telecaller' ? 'telecallerId' : 'executiveId']: userId
        }
      });

      if (response.ok) {
        const updatedLead = await response.json();
        setLead(updatedLead);
        alert(`${type} assigned successfully!`);
      }
    } catch (error) {
      console.error("Assignment error:", error);
    }
  };

  if (loading) return <MainLayout title="Loading..."><div style={{ padding: '24px' }}>Loading lead details...</div></MainLayout>;
  if (!lead) return <MainLayout title="Not Found"><div style={{ padding: '24px' }}>Lead not found</div></MainLayout>;

  // Data processing for Tier 1 and Tier 2 fields
  const allCustomKeys = lead.customFields ? Object.keys(lead.customFields) : [];
  
  // Filter out empty values for view mode
  const visibleKeys = allCustomKeys.filter(key => {
    const val = lead.customFields[key];
    return val !== '' && val !== null && val !== undefined && val !== 0;
  });

  // TIER 1: User defined in customFieldsDef
  // Actually, we should show all fields in customFieldsDef if we are in EDIT mode, 
  // but only non-empty ones in VIEW mode, OR just show all customFieldsDef in both so users see "Not set".
  // Let's show all customFieldsDef fields that exist in the settings template (Tier 1).
  const tier1Defs = customFieldsDef; 
  
  // TIER 2: Any key stored in customFields that is NOT in customFieldsDef
  // And has a non-empty value (since we shouldn't show a bunch of empty scraped keys)
  const tier2Keys = visibleKeys.filter(key => !customFieldsDef.find(d => d.name === key));


  return (
    <MainLayout title={lead.name}>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button 
            onClick={() => isEditing ? setIsEditing(false) : navigate(-1)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: 'white', 
              border: '1px solid var(--color-border-primary)', 
              padding: '6px 12px',
              borderRadius: 'var(--border-radius-md)',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            <ChevronLeft size={16} /> {isEditing ? 'Cancel' : 'Back'}
          </button>
          
          {isEditing ? (
            <input 
              name="name"
              value={editForm.name}
              onChange={handleEditChange}
              style={{ fontSize: '20px', fontWeight: 'bold', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-brand-primary)', width: '250px' }}
            />
          ) : (
            <div style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {lead.name}
              {lead.leadType && <Badge type={lead.leadType}>{lead.leadType}</Badge>}
            </div>
          )}
        </div>

        <div>
          {isEditing ? (
            <button 
              onClick={handleSaveEdit}
              disabled={savingEdit}
              style={{ padding: '8px 20px', background: 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: 'var(--border-radius-md)', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
            >
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </button>
          ) : (
            <button 
              onClick={handleEdit}
              style={{ padding: '8px 16px', background: 'white', border: '1px solid var(--color-border-primary)', borderRadius: 'var(--border-radius-md)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <PenSquare size={14} /> Edit Lead
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* CORE INFO */}
          <div className="card" style={{ background: 'white', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '20px', borderBottom: '1px solid var(--color-border-tertiary)', paddingBottom: '10px' }}>
              Lead Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {isEditing ? (
                <>
                  <EditableField label="Phone" name="phone" value={editForm.phone} onChange={handleEditChange} icon={<Phone size={14} />} />
                  <EditableField label="Email" name="email" value={editForm.email} onChange={handleEditChange} icon={<Mail size={14} />} />
                  <EditableField label="Source" name="source" value={editForm.source} onChange={handleEditChange} type="select" options={leadSources} />
                  <EditableField label="Temperature" name="leadType" value={editForm.leadType} onChange={handleEditChange} type="select" options={['', ...leadTypes]} />
                </>
              ) : (
                <>
                  <DetailField label="Phone" value={lead.phone} icon={<Phone size={14} />} />
                  <DetailField label="Email" value={lead.email || 'Not provided'} icon={<Mail size={14} />} />
                  <DetailField label="Source" value={<span style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>{lead.source}</span>} />
                  <DetailField label="Status" value={<span style={{ color: 'var(--color-brand-primary)', fontWeight: 'bold' }}>{lead.status}</span>} />
                  <DetailField label="Temperature" value={lead.leadType ? <span style={{ color: lead.leadType.toLowerCase() === 'hot' ? '#D85A30' : (lead.leadType.toLowerCase() === 'warm' ? '#EF9F27' : '#85B7EB'), fontWeight: 'bold' }}>{lead.leadType}</span> : 'Not Set'} />
                </>
              )}
            </div>
          </div>

          {/* TIER 1: YOUR CUSTOM FIELDS */}
          {(tier1Defs.length > 0) && (
            <div className="card" style={{ background: 'white', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '20px', borderBottom: '1px solid var(--color-border-tertiary)', paddingBottom: '10px' }}>
                Your Custom Fields
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {tier1Defs.map(field => (
                  isEditing ? (
                    <EditableField 
                      key={field._id || field.name}
                      label={field.name}
                      name={field.name}
                      value={editForm.customFields[field.name] || ''}
                      onChange={(e) => handleCustomEditChange(field.name, e.target.value)}
                      type={field.type}
                      options={field.options}
                    />
                  ) : (
                    <DetailField 
                      key={field._id || field.name}
                      label={field.name}
                      value={lead.customFields && lead.customFields[field.name] ? String(lead.customFields[field.name]) : 'N/A'}
                      icon={<FileText size={14} />}
                    />
                  )
                ))}
              </div>
            </div>
          )}

          {/* TIER 2: SCRAPED / SYSTEM DATA (READ-ONLY) */}
          {tier2Keys.length > 0 && (
            <div className="card" style={{ background: 'white', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', borderBottom: '1px solid var(--color-border-tertiary)', paddingBottom: '10px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 'bold', margin: 0 }}>
                  Scraped Data
                </h3>
                <span style={{ fontSize: '11px', background: '#E3F2FD', color: '#1976D2', padding: '2px 8px', borderRadius: '12px', fontWeight: '600' }}>
                  Auto-Generated
                </span>
              </div>
              
              {isEditing ? (
                <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', background: 'var(--color-background-secondary)', padding: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertCircle size={16} color="var(--color-text-tertiary)" />
                  Scraped data is read-only. Re-scrape the business to update these fields.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {tier2Keys.map(key => (
                    <DetailField 
                      key={key}
                      label={formatLabel(key)}
                      value={inferAndRenderValue(key, lead.customFields[key])}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ACTIVITY LOG */}
          <div className="card" style={{ background: 'white', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '20px' }}>Activity History</h3>
            <div style={{ position: 'relative', paddingLeft: '30px' }}>
              <div style={{ position: 'absolute', left: '10px', top: '0', bottom: '0', width: '2px', background: 'var(--color-border-tertiary)' }}></div>
              {lead.logs && lead.logs.length > 0 ? (
                lead.logs.slice().reverse().map((log, idx) => (
                  <div key={idx} style={{ position: 'relative', marginBottom: '24px' }}>
                    <div style={{ position: 'absolute', left: '-32px', top: '0', width: '32px', height: '32px', borderRadius: '50%', background: log.action.includes('Assigned') ? '#FFF9E6' : (log.action.includes('Created') ? '#EAF3DE' : '#E6F1FB'), display: 'flex', alignItems: 'center', justifyContent: 'center', border: '4px solid white', zIndex: 1 }}>
                      {log.action.includes('Created') ? <Plus size={14} /> : log.action.includes('Assigned') ? <Calendar size={14} /> : <Clock size={14} />}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {log.action}
                        <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', fontWeight: 'normal' }}>{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                        <span style={{ fontWeight: '500' }}>{log.performedByName}</span>: {log.details}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>No activity logs yet.</div>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* ASSIGNMENT */}
          {(role === 'super-admin' || role === 'manager' || role === 'executive') && (
            <div className="card" style={{ background: 'white', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '20px' }}>Lead Assignment</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '8px' }}>Delegate to Telecaller</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select value={selectedTelecaller} onChange={(e) => setSelectedTelecaller(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border-primary)' }}>
                      <option value="">None</option>
                      {employees.filter(u => u.role === 'telecaller').map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                    </select>
                    <button onClick={() => handleAssign('telecaller', selectedTelecaller)} disabled={!selectedTelecaller} style={{ padding: '8px 12px', background: !selectedTelecaller ? 'var(--color-text-tertiary)' : 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Assign</button>
                  </div>
                </div>
                {(role === 'super-admin' || role === 'manager') && (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '8px' }}>Assign to Executive</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select value={selectedExecutive} onChange={(e) => setSelectedExecutive(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border-primary)' }}>
                        <option value="">None</option>
                        {employees.filter(u => u.role === 'executive' || u.role === 'site-visit-agent').map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                      </select>
                      <button onClick={() => handleAssign('executive', selectedExecutive)} disabled={!selectedExecutive} style={{ padding: '8px 12px', background: !selectedExecutive ? 'var(--color-text-tertiary)' : 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Assign</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FOLLOW-UP */}
          <div className="card" style={{ background: 'white', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '20px' }}>Log New Follow-up</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '8px' }}>Update Status</label>
                <select value={followUpStatus} onChange={(e) => setFollowUpStatus(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border-primary)' }}>
                  {leadStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '8px' }}>Update Temperature</label>
                <select value={followUpLeadType} onChange={(e) => setFollowUpLeadType(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border-primary)' }}>
                  <option value="">None</option>
                  {leadTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '8px' }}>Follow-up Note</label>
                <textarea rows="3" value={followUpNote} onChange={(e) => setFollowUpNote(e.target.value)} placeholder="What happened?..." style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid var(--color-border-primary)', resize: 'none' }} />
              </div>
              <div style={{ background: 'var(--color-background-secondary)', padding: '12px', borderRadius: '8px', border: '1px solid var(--color-border-tertiary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setReminderEnabled(!reminderEnabled)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '13px' }}>
                    <span style={{ fontSize: '16px' }}>⏰</span> Set Reminder
                  </div>
                  <input type="checkbox" checked={reminderEnabled} readOnly style={{ cursor: 'pointer' }} />
                </div>
                {reminderEnabled && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--color-border-tertiary)' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '8px' }}>Date & Time</label>
                    <input 
                      type="datetime-local" 
                      value={reminderDateTime} 
                      onChange={(e) => setReminderDateTime(e.target.value)}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border-primary)', fontFamily: 'inherit' }}
                    />
                  </div>
                )}
              </div>
              <button onClick={handleAddFollowUp} disabled={savingFollowUp || !followUpNote} style={{ width: '100%', padding: '10px', background: savingFollowUp || !followUpNote ? 'var(--color-text-tertiary)' : 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer' }}>
                {savingFollowUp ? 'Saving...' : 'Save Follow-up'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

const DetailField = ({ label, value, icon }) => (
  <div>
    <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500', color: 'var(--color-text-primary)', fontSize: '13px' }}>
      {icon && <span style={{ color: 'var(--color-text-tertiary)' }}>{icon}</span>}
      {value || 'Not set'}
    </div>
  </div>
);

const EditableField = ({ label, name, value, onChange, icon, type = 'text', options = [], placeholder }) => (
  <div>
    <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {icon && <span style={{ color: 'var(--color-text-tertiary)' }}>{icon}</span>}
      {type === 'select' ? (
        <select name={name} value={value} onChange={onChange} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid var(--color-brand-primary)', outline: 'none', fontSize: '13px' }}>
          {options.map(opt => <option key={opt} value={opt}>{opt === '' ? 'None' : opt}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea name={name} value={value} onChange={onChange} placeholder={placeholder} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid var(--color-brand-primary)', outline: 'none', fontSize: '13px', minHeight: '60px' }} />
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid var(--color-brand-primary)', outline: 'none', fontSize: '13px' }} />
      )}
    </div>
  </div>
);

export default LeadDetail;
